import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { useNotification } from '../../../../hooks/useNotification';
import { UPDATE_GTW_PAYMENT } from '../../../../services/graphql/vendors/grease-traps/greaseTrapMutation';
import type { GreaseTrapPayment } from '../../../../types/greaseTrap';
import {
  convertToAmericanDateFormat,
  convertToAmericanTimeFormat,
} from '../../../../utils/convertTimestamp';
import Dropdown from '../../common/Inputs/Dropdown';
import InputCardField from '../../common/Inputs/InputCardField';
import Toggle from '../../common/Toggle';

const PAYMENT_TYPES = [
  { value: 'Check', label: 'Check' },
  { value: 'Cash', label: 'Cash' },
  { value: 'ACH', label: 'ACH' },
];

type Props = {
  payment: GreaseTrapPayment;
  setValue: (key: string, value: string | number | boolean | null) => void;
  onUpdatePayment: () => void;
};

export const GreaseTrapPaymentCard = ({
  payment,
  setValue,
  onUpdatePayment,
}: Props) => {
  const { t } = useTranslation('common');
  const { notifySaveError, notifySaveSuccess } = useNotification();
  const [updatePayment] = useMutation(UPDATE_GTW_PAYMENT);

  const convertToString = (value: string | number | null | boolean): string => {
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }

    if (value === null) {
      return 'false';
    }

    return String(value);
  };

  const handleUpdate = (
    key: keyof GreaseTrapPayment,
    value: string | number | null | boolean,
  ) => {
    setValue(key, value);

    updatePayment({
      variables: {
        id: payment.id,
        key,
        value: convertToString(value),
      },
      onCompleted: () => {
        notifySaveSuccess();
        onUpdatePayment();
      },
      onError: () => {
        notifySaveError();
      },
    });
  };

  return (
    <div className="box-shadow flex flex-col items-center justify-start gap-[0.2rem] rounded-lg px-6 py-4">
      <div className="flex w-full items-center justify-between gap-2 text-xs text-gray-400">
        <p>{convertToAmericanDateFormat(payment.createdAt.toString())}</p>
        <p>{convertToAmericanTimeFormat(payment.createdAt.toString())}</p>
      </div>
      <div className="flex max-w-[250px] flex-col gap-y-2">
        <InputCardField
          id={`amount-${payment.id}`}
          type="number"
          format="money"
          doNotUseSaveSelfButton
          label={t('vendor-grease-trap-card.payment-amount')}
          selectedValue={payment.amount}
          placeholder={payment.amount}
          onSelectChange={(val) => {
            setValue('amount', Number(val));
          }}
          onSave={() => {
            handleUpdate('amount', payment.amount);
          }}
        />
        <div className="flex items-center justify-between gap-2 border-b border-gray-400">
          <div className="truncate text-sm !font-normal text-gray-400">
            {t('vendor-grease-trap-card.payment-type')}
          </div>
          <Dropdown
            options={PAYMENT_TYPES}
            selectedValue={payment.payment_type || ''}
            placeholder={t('vendor-grease-trap-card.payment-type')}
            onSelectChange={(newValue) => {
              handleUpdate('payment_type', newValue);
            }}
            selectClassname="!font-normal"
          />
        </div>
        {payment.payment_type === 'Check' && (
          <InputCardField
            id={`check-${payment.id}`}
            type="text"
            doNotUseSaveSelfButton
            label={t('vendor-grease-trap-card.check-number')}
            selectedValue={payment.check_number}
            placeholder={payment.check_number}
            onSelectChange={(val) => {
              setValue('check_number', val as string);
            }}
            onSave={() => {
              handleUpdate('check_number', payment.check_number);
            }}
          />
        )}
        <InputCardField
          id={`transaction-${payment.id}`}
          type="text"
          doNotUseSaveSelfButton
          label={t('vendor-grease-trap-card.transaction-number')}
          selectedValue={payment.transaction_number}
          placeholder={payment.transaction_number}
          onSelectChange={(val) => {
            setValue('transaction_number', val as string);
          }}
          onSave={() => {
            handleUpdate('transaction_number', payment.transaction_number);
          }}
        />
        <div className="flex w-full items-center justify-between gap-2 border-b border-gray-400">
          <Toggle
            onSelectChange={(newValue) => {
              if (newValue) {
                handleUpdate('paid_at', new Date().toISOString());
              } else {
                handleUpdate('paid_at', null);
              }
            }}
            selectedValue={!!payment.paid_at}
            placeholder={t('vendor-grease-trap-card.received')}
            className="w-full"
            placeholderClassName="!text-gray-400"
          />
        </div>
      </div>
    </div>
  );
};

export default GreaseTrapPaymentCard;
